// Import style
import './style.scss';

// Import components
import Field from '@builder/field';

// Import utilities
import { html, render } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { until } from 'lit/directives/until.js';

const {
	isDefined,
	isEmpty
} = _uo.utility;

import {
	getConditionPostData,
	getConditionsGroupById,
	updateRemoteConditionsMeta,
	getConditionById,
	conditionPostDataHasFields,
	getAndSaveConditionFields
} from '@builder/item/conditions/utilities';

import { getIntegrationName } from '@builder/integrations/utilities';

import { parseDynamicSentence } from '@builder/utilities';
import Actions from '@builder/item/actions';

/**
 * Creates a modal with the options of the condition
 */
export default class ItemConditionsModalOptions {
	/**
	 * Create a reference to the dialog
	 *
	 * @type {Ref}
	 */
	$dialog = createRef();

	/**
	 * Create a reference to the dialog
	 *
	 * @type {Ref}
	 */
	$saveButton = createRef();

	/**
	 * Invokes the main methods that create the modal
	 *
	 * @param {Object} options                   The options
	 * @param {String} options.conditionsGroupId The ID of the conditions groups
	 * @param {String} options.integrationCode   The unique code of the integration
	 * @param {String} options.conditionCode     The unique code of the condition
	 * @param {String} options.conditionId       The unique ID of the condition. This is option,
	 *                                           and you can leave empty to create a new condition.
	 * @param {String} options.comingFrom        A string that identifies if this modal was created
	 *                                           using the "Filter" button, the "+ Add" button, or
	 *                                           if the user is editing a condition pill
	 * @return {undefined}
	 */
	constructor( { conditionsGroupId, integrationCode, conditionCode, conditionId, comingFrom } ) {
		// Get the conditions group
		const conditionsGroup = getConditionsGroupById( {
			conditionsGroupId: conditionsGroupId,
		} );

		// Check if it's defined
		if ( isEmpty( conditionsGroup ) ) {
			return;
		}

		// Save it
		this.conditionsGroup = conditionsGroup;

		// Save "coming from"
		// This will basically tell us if the user created this modal by clicking on the "Filter
		// button in actions, if they clicked on the "+Add" button in the conditions block, or
		// if they are editing a condition pill
		this.comingFrom = comingFrom;

		// Get condition
		// If conditionId is defined, then get it from the list of conditions
		if ( ! isEmpty( conditionId ) ) {
			// Get the data of the condition
			this.condition = getConditionById( {
				conditionsGroupId: conditionsGroupId,
				conditionId: conditionId,
			} );

			// Get condition code and integration code from condition
			if ( isDefined( this.condition ) ) {
				// Save unique identifiers
				this.integrationCode = this.condition.integrationCode;
				this.conditionCode = this.condition.conditionCode;
			}

			// Set the modal mode to "Edit"
			// This means the user is EDITING the condition, not creating a new one
			this.modalMode = 'edit';
		} else {
			// Save unique identifiers
			this.integrationCode = integrationCode;
			this.conditionCode = conditionCode;

			// Create a new condition
			this.condition = this.createCondition();

			// Set the modal mode to "Create"
			// This means the user is CREATING a condition, not editing one
			this.modalMode = 'create';
		}

		// Check if the condition exists
		// Check if the condition post data exists
		if ( ! isEmpty( this.condition ) && ! isEmpty( this.conditionPostData ) ) {
			// Create the dialog
			this.render();
		}
	}

	/**
	 * Renders the modal
	 *
	 * @return {undefined}
	 */
	render() {
		render(
			html`
				<uo-dialog
					heading="${ UncannyAutomator.i18n.conditions.configureTheRule }"
					force-manual-close
					size="large"
					open

					${ ref( this.$dialog ) }
				>
					<div
						class="uap-conditions-options"
						data-integration="${ this.integrationCode }"
						data-condition="${ this.conditionCode }"
					>
						${ this._templateHeader }

						${ until(
							this._templateFields,
							this._templateFieldsPlaceholder
						) }
					</div>

					<uo-button
						slot="primary-action"

						${ ref( this.$saveButton ) }

						@click=${ () => {
							// Save options
							this.saveOptions();
						} }
					>
						${ UncannyAutomator.i18n.conditions.saveFilter }
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"

						@click=${ () => {
							// Remove condition
							this.maybeRemoveCondition();

							// Maybe delete the conditions group
							this.maybeRemoveConditionsGroup();
						} }
					>
						${ UncannyAutomator.i18n.cancel }
					</uo-button>

				</uo-dialog>
			`,
			this.$wrapper,
			{ host: this }
		);
	}

	/**
	 * Returns the header of the template
	 *
	 * @return {TemplateResult} The TemplateResult of the header
	 */
	get _templateHeader() {
		return html`
			<div class="uap-conditions-options-header">
				<div class="uap-conditions-options-header-integration">
					<ua-builder-integration-icon
						code=${ this.condition.integrationCode }
						size="xsmall"
						plain
					></ua-builder-integration-icon>

					<uo-chip size="small" filled>
						${ getIntegrationName( this.condition.integrationCode ) }
					</uo-chip>
				</div>

				<div class="uap-conditions-options-header-sentence">
					${
						parseDynamicSentence( {
							dynamicSentence: this.conditionPostData.dynamic_name,
							fields: this.condition.fields,
							itemID: this.conditionsGroup.actionWithLowestPriority
						} )
					}
				</div>
			</div>
		`;
	}

	/**
	 * Returns the fields of the template
	 *
	 * @return {TemplateResult} The TemplateResult of the fields
	 */
	get _templateFields() {
		// We might need to do a REST call to get the fields, so make an async getter
		return ( async () => {

			try {

				// Check whether we have to get the fields of this condition first
				if ( ! conditionPostDataHasFields( {
					integrationCode: this.integrationCode,
					conditionCode: this.conditionCode
				} ) ) {
					// Get the fields
					await getAndSaveConditionFields( {
						integrationCode: this.integrationCode,
						conditionCode: this.conditionCode
					} );
				}

				// Remove uap class once we move everything to Web Components
				return html`
					<div class="uap uap-conditions-options-fields">
							${
								repeat(
									// Get integrations with conditions
									Object.values( this.fields ),
									( field ) => field.id,
									( field ) => html`${ field.$wrapper.get( 0 ) }`
								)
							}
						</div>
					</div>
				`;

			} catch ( e ) {
				return html`
					${ UncannyAutomator.i18n.somethingWentWrong }
				`;
			}

		} )();
	}

	/**
	 * Returns a placeholder to show while the fields load
	 *
	 * @return {TemplateResult} The TemplateResult of the template
	 */
	get _templateFieldsPlaceholder() {
		return html`
			<div class="uap-conditions-options-fields-placeholder">

				<div>
					<uo-placeholder width="100px"></uo-placeholder> <uo-placeholder width="30px"></uo-placeholder>
				</div>

				<div>
					<uo-placeholder height="42px"></uo-placeholder>
				</div>

				<div>
					<uo-placeholder width="40px"></uo-placeholder> <uo-placeholder width="100px"></uo-placeholder> <uo-placeholder width="50px"></uo-placeholder>
				</div>

			</div>
		`;
	}

	/**
	 * Saves options
	 *
	 * @return {undefined}
	 */
	saveOptions() {
		// Check if the fields are valid
		if ( this.areFieldsValid() ) {
			// Save fields in the condition
			this.condition.fields = this.getFieldsValues();

			// Add loading animation to the button
			this.$saveButton.value.loading = true;

			// Update the data in the server
			updateRemoteConditionsMeta( {
				onSuccess: ( response ) => {
					// Remove loading animation to the button
					this.$saveButton.value.loading = false;

					// Check if the task was successful
					if ( response.success ) {
						// Close the modal
						this.$dialog.value.close();
					}

					new Actions();
				},
				onFail: () => {
					// Remove loading animation to the button
					this.$saveButton.value.loading = false;
				},
			} );
		}
	}

	/**
	 * Creates the condition and adds it to the conditions group
	 *
	 * @return {Condition} The created condition
	 */
	createCondition() {
		// Add the condition
		return this.conditionsGroup.addCondition( {
			integrationCode: this.integrationCode,
			conditionCode: this.conditionCode,
			fields: {},
		} );
	}

	/**
	 * Returns the values of all the fields
	 *
	 * @return {Object} Object with the values of the fields
	 */
	getFieldsValues() {
		// Default response
		const values = {};

		// Iterate fields
		Object.values( this.fields ).forEach( ( field ) => {
			// Get and save the value
			values[ field.id ] = field.value;

			// If it's a select, get the option text and save it as "readable"
			if ( field.type == 'select' ) {
				// Get the custom field, if it has one
				if ( field.value === 'automator_custom_value' ) {
					// eslint-disable-next-line camelcase
					values[ `${ field.id }_custom` ] = field.getSelectCustomValue();
					// eslint-disable-next-line camelcase
					values[ `${ field.id }_readable` ] = values[ `${ field.id }_custom` ];
				} else {
					// Get the option text
					// eslint-disable-next-line camelcase
					values[ `${ field.id }_readable` ] = field.readableValue;
				}
			}

			try {
				// Check if we should save the label of the field to use it later in the sentence
				if ( field.attributes.showLabelInSentence ) {
					// eslint-disable-next-line camelcase
					values[ `${ field.id }_label` ] = field.attributes.label;
				}
			} catch ( e ) {
				if ( UncannyAutomator.dev.developerMode ) {
					// eslint-disable-next-line no-console
					console.warn( e );
				}
			}
		} );

		return values;
	}

	/**
	 * Validates the fields
	 *
	 * @return {Boolean} True if the fields are valid
	 */
	areFieldsValid() {
		// Default value
		let fieldsAreValid = true;

		// Iterate fields
		Object.values( this.fields ).forEach( ( field ) => {
			// Validate field
			const fieldValidation = field.validate();

			// Evaluate global validation
			fieldsAreValid = fieldsAreValid && fieldValidation.isValid;
		} );

		return fieldsAreValid;
	}

	/**
	 * Remove the created condition
	 *
	 * @return {undefined}
	 */
	maybeRemoveCondition() {
		// Check if the user was CREATING the condition
		if ( this.modalMode == 'create' ) {
			// Remove the condition using the condition ID
			this.conditionsGroup.removeCondition( {
				conditionID: this.condition.id,
			} );
		}
	}

	/**
	 * Checks if the conditions group doesn't have any conditions and removes it
	 *
	 * @return {undefined}
	 */
	maybeRemoveConditionsGroup() {
		// Check if the user clicked this modal by clicking on the "Filter" button in actions
		// We're deleting the conditions group if they come from the "Filter" button in actions because
		// we don't want to keep the group created after they click on "Cancel"
		if ( this.comingFrom == 'action-filter-button' ) {
			// Check if the conditions groups have conditions
			if ( ! this.conditionsGroup.hasConditions ) {
				// Remove it
				this.conditionsGroup.destroy();
			}
		}
	}

	/**
	 * The wrapper where we're going to add the dialog
	 * We're going to use this until the whole UI moves to Lit
	 *
	 * @return {Node} A wrapper
	 */
	get $wrapper() {
		// Check if we already created one
		if ( ! isDefined( this.#$wrapper ) ) {
			// Create the element
			this.#$wrapper = document.createElement( 'div' );

			// Append it to the DOM
			document.body.append( this.#$wrapper );
		}

		// Return the wrapper
		return this.#$wrapper;
	}

	/**
	 * Returns an object with the condition fields
	 *
	 * @return {Object} Object of Field instances
	 */
	get fields() {
		// Check if we already created the Field instances
		if ( isDefined( this._fields ) ) {
			return this._fields;
		}

		// Check if we already have the condition fields in the post data
		if ( ! isDefined( this.conditionPostData.fields ) ) {
			return null;
		}

		// Iterate the fields in the post data
		this._fields = {};

		this.conditionPostData.fields.forEach( ( fieldAttributes ) => {
			// Clone the attributes before doing modifications to them
			const fieldAttrs = Object.assign( {}, fieldAttributes );

			// Set action with lowest priority as the action used to scope the tokens
			fieldAttrs.itemId = this.conditionsGroup.actionWithLowestPriority;

			// Check if there is a value to prepopulate this field
			if ( isDefined( this.condition.fields[ fieldAttrs.optionCode ] ) ) {
				// Set current value
				fieldAttrs.currentValue = this.condition.fields[ fieldAttrs.optionCode ];

				// Check if it's a custom select
				if ( fieldAttrs.currentValue === 'automator_custom_value' ) {
					fieldAttrs.customValue = this.condition.fields[ `${ fieldAttrs.optionCode }_custom` ];
				}
			}

			// Create field instance
			const field = new Field( fieldAttrs.fieldType, fieldAttrs );

			// Init libraries
			// Remove once fields are web components
			// Do AJAX
			field.doAJAX();
			// Initilize the token selector
			field.initTokenSelector();
			// Initilize flatpickr
			field.initFlatpickr();
			// Initilize CodeMirror
			field.initCodeMirror();

			// Save field instance
			this._fields[ fieldAttrs.optionCode ] = field;
		} );

		return this._fields;
	}

	/**
	 * Returns the condition post data
	 *
	 * @return {Object} Object with useful data about the condition (like sentence and fields)
	 */
	get conditionPostData() {
		return getConditionPostData( {
			integrationCode: this.integrationCode,
			conditionCode: this.conditionCode,
		} );
	}

	#$wrapper;
}
